import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { auth as authApi, setTokens, clearTokens, getRefreshToken, isDemoMode, disableDemo } from '../services/api';

const AuthContext = createContext();

/** Inactivity timeout in ms (e.g. 30 min). Log out if no activity for this long. */
const INACTIVITY_MS = 30 * 60 * 1000;
/** How often to check inactivity (e.g. every 1 min). */
const INACTIVITY_CHECK_MS = 60 * 1000;

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);
  const lastActivityRef       = useRef(Date.now());
  const logoutRef             = useRef(null);

  // Restore session from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('zm_user');
    if (stored) {
      try { setUser(JSON.parse(stored)); } catch (_) {}
    }
    setLoading(false);
  }, []);

  // Keep logout ref up to date so inactivity timer can call it
  const logout = useCallback(async () => {
    const rt = getRefreshToken();
    if (rt && !isDemoMode()) {
      try { await authApi.logout(rt); } catch (_) {}
    }
    disableDemo();
    clearTokens();
    setUser(null);
    localStorage.removeItem('zm_user');
  }, []);
  logoutRef.current = logout;

  // Listen for forced logout events (token refresh failure)
  useEffect(() => {
    const handler = () => {
      setUser(null);
      localStorage.removeItem('zm_user');
    };
    window.addEventListener('zm:logout', handler);
    return () => window.removeEventListener('zm:logout', handler);
  }, []);

  // Inactivity timeout: log out after INACTIVITY_MS with no input (skip demo / logged-out)
  useEffect(() => {
    if (!user || isDemoMode()) return undefined;

    lastActivityRef.current = Date.now();
    const bump = () => { lastActivityRef.current = Date.now(); };

    const checkIdle = () => {
      if (Date.now() - lastActivityRef.current >= INACTIVITY_MS && logoutRef.current) {
        logoutRef.current();
      }
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') checkIdle();
    };

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach((e) => document.addEventListener(e, bump, { capture: true, passive: true }));
    window.addEventListener('focus', checkIdle);
    document.addEventListener('visibilitychange', onVisible);

    const interval = setInterval(checkIdle, INACTIVITY_CHECK_MS);
    checkIdle();

    return () => {
      events.forEach((e) => document.removeEventListener(e, bump, { capture: true }));
      window.removeEventListener('focus', checkIdle);
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(interval);
    };
  }, [user]);

  const persistUser = (u) => {
    setUser(u);
    localStorage.setItem('zm_user', JSON.stringify(u));
  };

  // ── Login ──────────────────────────────────────────────────────────────
  const login = useCallback(async (email, password) => {
    const res = await authApi.login(email, password);
    setTokens(res.data.access_token, res.data.refresh_token);
    persistUser(res.data.user);
    return res.data.user;
  }, []);

  // ── Register customer ──────────────────────────────────────────────────
  const register = useCallback(async (payload) => {
    const res = await authApi.register(payload);
    // Movers don't receive a token — they must wait for approval
    if (res.data.access_token) {
      setTokens(res.data.access_token, res.data.refresh_token);
      persistUser(res.data.user);
    }
    return res;
  }, []);

  const updateUser = useCallback((patch) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      localStorage.setItem('zm_user', JSON.stringify(next));
      return next;
    });
  }, []);

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    updateUser,
    isAuthenticated: !!user,
    isAdmin:         user?.role === 'admin',
    isMover:         user?.role === 'mover',
    isCustomer:      user?.role === 'customer',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
